import { useCallback, useEffect, useMemo, useState } from "react";
import { FlatList, Pressable, ScrollView, Text, TextInput, TextInputChangeEvent, View } from "react-native";
import { axiosInstance } from "../../api/Middleware";
import { useSessionStore } from "../../lib/Store/sessionStore.component";
import { CET, SaisieDuree } from "./GestionCET.types";
import dayjs from "dayjs";
import { Quotas } from "../../api/Dotation/types";
import useGestionCETStyle from "./GestionCET.styles";
import useEnTetePage from "../../components/EnTetePage/EnTetePage";
import DotationEpargnable from "./DotationEpargnable";
import Dropdown from "../../components/Dropdown/Dropdown";

export default function GestionCET() {
    const { getCurrentInfos } = useSessionStore();
    const { currentEntite, currentLogin } = getCurrentInfos();
    const { styles } = useGestionCETStyle();
    const { EnTete } = useEnTetePage({text: "Mon Compte Épargne Temps"})
    const [soldes, setSoldes] = useState<CET>()
    const [dotationsEpargnables, setDotationsEpargnables] = useState<Quotas[]>([]);
    const [selectedDotation, setSelectedDotation] = useState<Quotas>();
    const [duree, setDuree] = useState<SaisieDuree>({jours: '', heures: '', minutes: ''});
    const resteSolde = useMemo(() => {
        if(soldes){
            return soldes.epargnes - soldes.pris - soldes.payes - soldes.rafp
        }
        return null;
    }, [soldes])
    const validationDisabled = useMemo(() => (
        !selectedDotation
        || (selectedDotation?.jourHeure === 1 && duree.jours === '')
        || (selectedDotation?.jourHeure === 2 && duree.heures === '' && duree.minutes === '')
    ), [duree.heures, duree.jours, duree.minutes, selectedDotation]);
    const handleSaisieJours = useCallback((e: TextInputChangeEvent) => {
        if(e.nativeEvent.text.match(/^[1-9][0-9]*([.,]5{0,1}){0,1}$/) || e.nativeEvent.text === ''){
            setDuree((old) => ({...old, jours: e.nativeEvent.text}))
        }
    }, [])
    const handleSaisieHeures = useCallback((e: TextInputChangeEvent) => {
        if(e.nativeEvent.text.match(/^[1-9][0-9]*$/) || e.nativeEvent.text === ''){
            setDuree((old) => ({...old, heures: e.nativeEvent.text}))
        }
    },[])
    const handleSaisieMinutes = useCallback((e: TextInputChangeEvent) => {
        if(e.nativeEvent.text.match(/^(([1-5][0-9])||([1-9]))$/) || e.nativeEvent.text === ''){
            setDuree((old) => ({...old, minutes: e.nativeEvent.text}))
        }
    },[])
    useEffect(() => {
        axiosInstance.get(`/personnel/personnel/cets?identifiantEntite=${currentEntite?.identifiantEntite}&identifiantAgent=${currentLogin?.identifiant}`)
            .then((response) => setSoldes(response.data.data[0]))
        axiosInstance.get(`/personnel/personnel/naturesabsences/epargnables-cet?identifiantEntite=${currentEntite?.identifiantEntite}&identifiantAgent=${currentLogin?.identifiant}`)
            .then((response) => setDotationsEpargnables(response.data))
    }, [currentEntite?.identifiantEntite, currentLogin?.identifiant])
    return(
        <ScrollView contentContainerStyle={styles.container}>
            {EnTete}
            <View style={styles.modernCard}>
                <Text style={styles.title}>Etat du compte au {dayjs().format('DD/MM/YYYY')}</Text>
                <View style={styles.mainInfo}>
                    <View style={styles.primaryStat}>
                    <Text style={styles.primaryLabel}>Il vous reste</Text>
                    {/* TODO: ajouter la couleur de la nature d'absence CET */}
                    <Text style={[styles.primaryValue]}>
                        {resteSolde} {soldes?.jourHeures === 1 ? "jours" : "heures"}
                    </Text>
                    </View>
                </View>

                <View style={styles.quickStats}>
                    <View style={styles.statItem}>
                        <Text style={styles.statLabel}>Epargnés</Text>
                        <Text style={styles.statValue}>{soldes?.epargnes}</Text>
                    </View>
                    <View style={styles.statItem}>
                        <Text style={styles.statLabel}>Pris</Text>
                        <Text style={styles.statValue}>{soldes?.pris}</Text>
                    </View>
                    <View style={styles.statItem}>
                        <Text style={styles.statLabel}>Payés</Text>
                        <Text style={styles.statValue}>{soldes?.payes}</Text>
                    </View>

                    <View style={styles.statItem}>
                        <Text style={styles.statLabel}>R.A.F.P.</Text>
                        <Text style={styles.statValue}>{soldes?.rafp}</Text>
                    </View>
                </View>
            </View>
            <View style={styles.subContent}>
                <View style={styles.column}>
                    <Text>Demande d'épargne CET</Text>
                    <View style={[styles.modernCard, styles.form]}>
                        <View style={styles.formElement}>
                            <Text style={styles.mainText}>Nature d'absence</Text>

                            <Dropdown
                                value={selectedDotation?.natureAbsence?.libelle}
                                options={dotationsEpargnables}
                                emptyText="Aucune nature d'absence"
                                getText={(quota) => quota.natureAbsence?.libelle}
                                onSelectOption={(quota) => setSelectedDotation(quota)}
                                style={styles.formInput}
                            />
                        </View>
                        <View style={styles.formElement}>
                            <Text style={styles.mainText}>Durée</Text>
                            <View style={styles.dureeContainer}>
                                {selectedDotation?.jourHeure === 2 ? (
                                    <>
                                        <View style={styles.dureeElement}>
                                            <TextInput value={duree.heures} style={styles.formInput} onChange={handleSaisieHeures}/>
                                            <Text>heures</Text>
                                        </View>
                                        <View style={styles.dureeElement}>
                                            <TextInput value={duree.minutes} style={styles.formInput} onChange={handleSaisieMinutes}/>
                                            <Text>minutes</Text>
                                        </View>
                                    </>
                                ) : (
                                    <View style={styles.dureeElement}>
                                        <TextInput value={duree.jours} style={styles.formInput} onChange={handleSaisieJours}/>
                                        <View style={styles.texteJours}>
                                            <Text>jours</Text>
                                            <Text style={styles.indications}>(par tranche de 0,5 jours)</Text>
                                        </View>
                                    </View>
                                )}
                            </View>
                        </View>
                        <View style={styles.formElement}>
                            <Pressable style={[styles.buttonAccept, validationDisabled && styles.buttonAcceptDisabled]} disabled={validationDisabled}>
                                <Text style={styles.buttonText}>Valider</Text>
                            </Pressable>
                        </View>
                    </View>
                </View>
                <View style={styles.column}>
                    <Text>Dotations épargnables</Text>
                    <FlatList data={dotationsEpargnables} renderItem={({item}) => <DotationEpargnable dotation={item}/>} contentContainerStyle={styles.dotationsContainer} />
                </View>
            </View>
        </ScrollView>
    )
}
